
  import { defineComponent, onMounted, ref, computed } from 'vue';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import { reinitializeComponents } from '@/core/plugins/keenthemes';
  import moment from 'moment';
  import Multiselect from '@vueform/multiselect';
  import XLSX from 'xlsx-js-style';

  export default defineComponent({
    name: 'auction-properties-report',
    components: { Multiselect },
    async setup() {
      const showExportBtn = ref(false);
      const data = ref<{ data: Array<any> } | null>(null);
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const { can } = useAbility();

      const selectedItems = ref<{
        dateFrom: string;
        dateTo: string;
        organiserIds: number[];
        areaIds: number[];
        onlyFullySold: boolean;
        excludeAreaIds: number[];
        excludeTypeIds: number[];
        excludeCategoryIds: number[];
      }>({
        dateFrom: '',
        dateTo: '',
        organiserIds: [],
        areaIds: [],
        onlyFullySold: false,
        excludeAreaIds: [],
        excludeTypeIds: [],
        excludeCategoryIds: [],
      });

      const translate = (text: string): string => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const isFormValid = computed(() => {
        return (
          selectedItems.value.dateFrom !== '' &&
          selectedItems.value.dateTo !== ''
        );
      });

      const excelFileName = computed(() => {
        const dateFrom = selectedItems.value.dateFrom
          ? moment(selectedItems.value.dateFrom).format('YYYY-MM-DD')
          : '';
        const dateTo = selectedItems.value.dateTo
          ? moment(selectedItems.value.dateTo).format('YYYY-MM-DD')
          : '';
        return `تقرير عقارات المزادات ${dateFrom} - ${dateTo}.xlsx`;
      });

      const areas = computed(() => store.getters.allListAreas || []);
      const organizers = computed(
        () => store.getters.auctionOrganisesList || []
      );
      const propertyTypes = computed(
        () => store.getters.allAuctionPropertyTypes || []
      );
      const categories = computed(
        () => store.getters.allAuctionsDescriptionsList || []
      );

      const exportToExcel = () => {
        if (!data.value || !data.value.data || data.value.data.length === 0) {
          return;
        }

        const reportData = data.value.data;

        // Build filter title lines
        const filterLines: string[] = [];
        filterLines.push(
          `الفترة: من ${selectedItems.value.dateFrom} إلى ${selectedItems.value.dateTo}`
        );

        // Areas
        if (selectedItems.value.areaIds.length > 0) {
          const selectedAreas = areas.value
            .filter((a) => selectedItems.value.areaIds.includes(a.id))
            .map((a) => a.titleAr || a.title || a.name || '')
            .filter((name) => name)
            .join('، ');
          if (selectedAreas) {
            filterLines.push(`المناطق: ${selectedAreas}`);
          }
        }

        if (selectedItems.value.organiserIds.length > 0) {
          const selectedOrgs = organizers.value
            .filter((o) => selectedItems.value.organiserIds.includes(o.id))
            .map((o) => o.titleAr || o.title || o.name || '')
            .filter((name) => name)
            .join('، ');
          if (selectedOrgs) filterLines.push(`المنظمين: ${selectedOrgs}`);
        }

        if (selectedItems.value.onlyFullySold) {
          filterLines.push('نسبة البيع: 100% فقط');
        }

        if (selectedItems.value.excludeAreaIds.length > 0) {
          const excludedAreas = areas.value
            .filter((a) => selectedItems.value.excludeAreaIds.includes(a.id))
            .map((a) => a.titleAr || a.title || a.name || '')
            .filter((name) => name)
            .join('، ');
          if (excludedAreas)
            filterLines.push(`مناطق مستثناة: ${excludedAreas}`);
        }

        if (selectedItems.value.excludeTypeIds.length > 0) {
          const excludedTypes = propertyTypes.value
            .filter((pt) => selectedItems.value.excludeTypeIds.includes(pt.id))
            .map((pt) => pt.title || pt.titleAr || '')
            .filter((name) => name)
            .join('، ');
          if (excludedTypes)
            filterLines.push(`أنواع مستثناة: ${excludedTypes}`);
        }

        if (selectedItems.value.excludeCategoryIds.length > 0) {
          const excludedCats = categories.value
            .filter((c) =>
              selectedItems.value.excludeCategoryIds.includes(c.id)
            )
            .map((c) => c.titleAr || c.title || '')
            .filter((name) => name)
            .join('، ');
          if (excludedCats) filterLines.push(`فئات مستثناة: ${excludedCats}`);
        }

        // Headers (RTL order)
        const headers = [
          'التاريخ',
          'الوقت',
          'المنظم',
          'موقع المزاد',
          'المنطقة',
          'فئة العقار',
          'نوع العقار',
          'القطعة',
          'القسيمة',
          'المساحة (م²)',
          'الموقع',
          'سعر الابتداء',
          'سعر الابتداء/م²',
          'سعر البيع',
          'سعر البيع/م²',
          'نسبة الزيادة',
          'نسبة التوريد',
          'نسبة البيع',
          'حالة العقار',
          'طلب تمويل',
          'طلب شيك مصدق',
          'موقع المزاد',
          'آخر تحديث',
          'رابط العقار',
        ];

        const numCols = headers.length;

        // Common border style
        const border = {
          top: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } },
        };

        const headerStyle = {
          font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 12 },
          fill: { patternType: 'solid', fgColor: { rgb: '4472C4' } },
          alignment: {
            horizontal: 'center',
            vertical: 'center',
            wrapText: true,
          },
          border,
        };

        const dataStyle = {
          font: { sz: 11 },
          alignment: {
            horizontal: 'center',
            vertical: 'center',
            wrapText: true,
          },
          border,
        };

        const numberStyle = {
          font: { sz: 11 },
          alignment: { horizontal: 'center', vertical: 'center' },
          border,
          numFmt: '#,##0',
        };

        const titleStyle = {
          font: { bold: true, sz: 14, color: { rgb: '1F4E79' } },
          alignment: { horizontal: 'right', vertical: 'center' },
        };

        const filterStyle = {
          font: { sz: 11, color: { rgb: '595959' } },
          alignment: { horizontal: 'right', vertical: 'center' },
        };

        type CellStyle =
          | typeof headerStyle
          | typeof dataStyle
          | typeof numberStyle
          | typeof titleStyle
          | typeof filterStyle;

        const wsData: { v: string | number; s: CellStyle }[][] = [];
        const merges: XLSX.Range[] = [];

        // Title row
        const titleRow: { v: string | number; s: CellStyle }[] = Array(numCols)
          .fill(null)
          .map(() => ({ v: '', s: titleStyle }));
        titleRow[0] = { v: 'تقرير عقارات المزادات', s: titleStyle };
        wsData.push(titleRow);
        merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } });

        // Filter rows
        filterLines.forEach((line, idx) => {
          const filterRow: { v: string | number; s: CellStyle }[] = Array(
            numCols
          )
            .fill(null)
            .map(() => ({ v: '', s: filterStyle }));
          filterRow[0] = { v: line, s: filterStyle };
          wsData.push(filterRow);
          merges.push({
            s: { r: idx + 1, c: 0 },
            e: { r: idx + 1, c: numCols - 1 },
          });
        });

        // Empty row before headers
        wsData.push(
          Array(numCols)
            .fill(null)
            .map(() => ({ v: '', s: dataStyle }))
        );

        // Header row
        wsData.push(headers.map((h) => ({ v: h, s: headerStyle })));

        // Data rows
        reportData.forEach((item) => {
          const row = [
            { v: item.date ?? '', s: dataStyle },
            { v: item.time ?? '', s: dataStyle },
            { v: item.organizerName ?? '', s: dataStyle },
            { v: item.venue ?? '', s: dataStyle },
            { v: item.area ?? '', s: dataStyle },
            { v: item.propertyType ?? '', s: dataStyle },
            { v: item.propertyDescription ?? '', s: dataStyle },
            { v: item.block ?? '', s: dataStyle },
            { v: item.plot ?? '', s: dataStyle },
            {
              v: item.size !== '' && item.size != null ? Number(item.size) : '',
              s: numberStyle,
            },
            { v: item.address ?? item.propertyLocation ?? '', s: dataStyle },
            {
              v:
                item.startingPrice !== '' && item.startingPrice != null
                  ? Number(String(item.startingPrice).replace(/,/g, ''))
                  : '',
              s: numberStyle,
            },
            {
              v:
                item.startingPricePerSqrM !== '' &&
                item.startingPricePerSqrM != null
                  ? Number(String(item.startingPricePerSqrM).replace(/,/g, ''))
                  : '',
              s: numberStyle,
            },
            {
              v:
                item.salePrice !== '' && item.salePrice != null
                  ? Number(String(item.salePrice).replace(/,/g, ''))
                  : '',
              s: numberStyle,
            },
            {
              v:
                item.salePricePerSqrM !== '' && item.salePricePerSqrM != null
                  ? Number(String(item.salePricePerSqrM).replace(/,/g, ''))
                  : '',
              s: numberStyle,
            },
            {
              v:
                item.percentagePremiumPaid !== '' &&
                item.percentagePremiumPaid != null
                  ? `${item.percentagePremiumPaid}%`
                  : '',
              s: dataStyle,
            },
            { v: item.supplyPercentage ?? '', s: dataStyle },
            {
              v:
                item.salePercentage !== '' && item.salePercentage != null
                  ? `${item.salePercentage}%`
                  : '',
              s: dataStyle,
            },
            { v: item.auctionsStatus ?? '', s: dataStyle },
            { v: item.loanRequest ? 'نعم' : 'لا', s: dataStyle },
            { v: item.certifiedCheckRequest ? 'نعم' : 'لا', s: dataStyle },
            { v: item.auctionLocation ?? '', s: dataStyle },
            { v: item.lastUpdate ?? '', s: dataStyle },
            { v: item.deepLinkUrl ?? '', s: dataStyle },
          ];
          wsData.push(row);
        });

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws['!merges'] = merges;

        ws['!cols'] = [
          { wch: 12 }, // التاريخ
          { wch: 8 }, // الوقت
          { wch: 20 }, // المنظم
          { wch: 30 }, // المقر
          { wch: 14 }, // المنطقة
          { wch: 12 }, // فئة العقار
          { wch: 14 }, // نوع العقار
          { wch: 10 }, // القطعة
          { wch: 10 }, // القسيمة
          { wch: 12 }, // المساحة
          { wch: 30 }, // الموقع
          { wch: 16 }, // سعر الابتداء
          { wch: 16 }, // سعر الابتداء/م²
          { wch: 16 }, // سعر البيع
          { wch: 16 }, // سعر البيع/م²
          { wch: 14 }, // نسبة الزيادة
          { wch: 14 }, // نسبة التوريد
          { wch: 12 }, // نسبة البيع
          { wch: 40 }, // حالة العقار
          { wch: 14 }, // طلب تمويل
          { wch: 18 }, // طلب شيك مصدق
          { wch: 30 }, // موقع المزاد
          { wch: 14 }, // آخر تحديث
          { wch: 60 }, // رابط العقار
        ];

        ws['!rows'] = [{ hpt: 35 }];

        const wb = XLSX.utils.book_new();
        if (!wb.Workbook) wb.Workbook = {};
        if (!wb.Workbook.Views) wb.Workbook.Views = [];
        wb.Workbook.Views[0] = { RTL: true };

        XLSX.utils.book_append_sheet(wb, ws, 'عقارات المزادات');
        XLSX.writeFile(wb, excelFileName.value);
      };

      const onSubmitCreate = async () => {
        if (!selectedItems.value.dateFrom || !selectedItems.value.dateTo) {
          Swal.fire({
            text: translate('dateRangeRequired'),
            icon: 'warning',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-warning',
            },
          });
          return;
        }

        const fromDate = moment(selectedItems.value.dateFrom);
        const toDate = moment(selectedItems.value.dateTo);
        if (toDate.isBefore(fromDate)) {
          Swal.fire({
            text: translate('dateToMustBeAfterDateFrom'),
            icon: 'warning',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-warning',
            },
          });
          return;
        }

        const formattedParams = {
          value: {
            ...selectedItems.value,
            dateFrom: fromDate.format('YYYY-MM-DD'),
            dateTo: toDate.format('YYYY-MM-DD'),
          },
        };

        if (submitButton.value) {
          submitButton.value.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        try {
          await store.dispatch(
            Actions.GET_AUCTION_PROPERTIES_REPORT,
            formattedParams
          );
          data.value = store.getters.auctionPropertiesReportData;

          if (data.value && data.value.data && data.value.data.length > 0) {
            showExportBtn.value = true;
            Swal.fire({
              text: translate('SUCCESSFULLY_GET_REPORTS_DATA'),
              icon: 'success',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-primary',
              },
            });
          } else {
            showExportBtn.value = false;
            Swal.fire({
              text: translate('noDataFound'),
              icon: 'info',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-info',
              },
            });
          }
        } catch (error) {
          const [errorName] = Object.keys(store.getters.getErrors);
          const errorMsg = store.getters.getErrors[errorName];

          Swal.fire({
            text: translate(errorMsg?.[0] || 'errorOccurred'),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
        } finally {
          submitButton.value?.removeAttribute('data-kt-indicator');
          if (submitButton.value) {
            submitButton.value.disabled = false;
          }
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('auctionPropertiesReport'), [
          translate('Reports'),
        ]);
      });

      // Load DDL data
      await store.dispatch(Actions.GET_ALL_AREAS_LIST, { type: 'auction' });
      await store.dispatch(Actions.GET_ALL_AUCTION_ORGANISERS);
      await store.dispatch(Actions.GET_ALL_AUCTION_PROPERTY_TYPES);
      await store.dispatch(Actions.GET_ALL_AUCTION_DESCRIPTIONS);

      reinitializeComponents();
      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        areas,
        organizers,
        propertyTypes,
        categories,
        excelFileName,
        showExportBtn,
        data,
        selectedItems,
        onSubmitCreate,
        exportToExcel,
        submitButton,
        translate,
        goBack,
        can,
        isFormValid,
      };
    },
  });
